import { httpClient } from '../httpClient/httpClient';
import { EntityType } from './trackerApi';

export enum TagPriority {
  Default = 0,
  High = 1,
}

export interface SlimTag {
  id: number;
  label: string;
  color: string;
  userId?: number | null;
  added?: string;
  addedByUserName?: string;
}

export interface EntityTag {
  id: number;
  label: string;
  color: string;
  userId: number | null;
  archived: string | null;
  systemTag: boolean;
  entityCounts: Record<EntityType, number>;
  applicableEntityTypes: EntityType[] | null;
}

export interface EntityTagLink {
  entityTagId: number;
  entityId: number;
  entityType: EntityType;
  added: string;
  priorityLevel: TagPriority;
  addedByUserId: number | null;
}

export interface EntityTagWithLink {
  id: number;
  label: string;
  color: string;
  userId: number | null;
  systemTag: boolean;
  priorityLevel: TagPriority;
  added: string;
  addedByUserId: number | null;
}

export interface CreateTagRequest {
  label: string;
  color: string;
  systemTag?: boolean;
  isShared?: boolean;
  applicableEntityTypes?: EntityType[] | null;
}

export interface UpdateTagRequest {
  label: string;
  color: string;
  systemTag?: boolean;
  isShared?: boolean;
  applicableEntityTypes?: EntityType[] | null;
}

export interface LinkToTagRequest {
  tagId: number;
  entityIds: number[];
  entityType: EntityType;
  priorityLevel?: TagPriority;
}

export interface RemoveTagLinksRequest {
  tagId: number;
  entityIds: number[];
  entityType: EntityType;
}

export interface UpdateLinkedTagsRequest {
  entityId: number;
  entityType: EntityType;
  tagIdsToAdd: number[];
  tagIdsToRemove: number[];
}

export interface GetArticleTagsRequest {
  articleIds: number[];
  includeSystemTags?: boolean;
}

export interface ArticleTags {
  articleId: number;
  tags: SlimTag[];
}

export interface EntityTagCounts {
  entityCounts: Record<EntityType, number>;
  totalCount: number;
}

export const tagsApi = {
  get: (id: number) => httpClient.get<EntityTag>(`tags/${id}`),
  // Include system tags defaults to true on server depending on user role
  getAll: (params?: {
    includeArchived?: boolean;
    includeSystemTags?: boolean;
    filterByEntityType?: EntityType;
  }) => httpClient.get<EntityTag[]>(`tags`, { params }),
  getEntityTags: (entityType: EntityType, entityId: number) =>
    httpClient.get<EntityTagWithLink[]>(
      `tags/entity/${entityType}/${entityId}`,
    ),
  create: (request: CreateTagRequest) =>
    httpClient.post<EntityTag>(`tags`, { data: request }),
  update: (id: number, request: UpdateTagRequest) =>
    httpClient.put<EntityTag>(`tags/${id}`, { data: request }),
  archive: (id: number) => httpClient.put(`tags/${id}/archive`),
  restore: (id: number) => httpClient.put<EntityTag>(`tags/${id}/restore`),
  getEntityCounts: (id: number) =>
    httpClient.get<EntityTagCounts>(`tags/${id}/entity-counts`),
  linkToTag: (request: LinkToTagRequest) =>
    httpClient.post<EntityTagLink[]>(`tags/link`, { data: request }),
  removeTagLinks: (request: RemoveTagLinksRequest) =>
    httpClient.post(`tags/link/remove`, { data: request }),
  updateLinkedTags: (request: UpdateLinkedTagsRequest) =>
    httpClient.post(`tags/link/update`, { data: request }),
  // Include system tags defaults to false on server
  getArticleTags: (request: GetArticleTagsRequest) =>
    httpClient.post<ArticleTags[]>(`tags/articles`, { data: request }),
};
